import { useEffect, useRef, useState } from 'react';
import { activities } from './data/activities';
import {
  getCityFromCoords,
  getCoordinatesFromCity,
  getCoordinatesFromZip,
} from './services/weatherService';
import { savedCount } from './services/savedPlacesService';
import { track } from './services/firebase';
import useLocalForecast from './hooks/useLocalForecast';
import ActivityDiscoveryView from './components/ActivityDiscoveryView';
import SavedPlacesView from './components/SavedPlacesView';
import LocationInput from './components/LocationInput';
import LocalForecast from './components/LocalForecast';

const DEFAULT_AREA = { lat: 41.8616, lon: -74.1697, city: 'Stone Ridge, NY' };
const OUTINGS = [
  {
    id: 'hiking',
    title: 'Find a hike',
    description: 'Woodland trails, ridgelines, and a little room to wander.',
  },
  {
    id: 'fishing',
    title: 'Go fishing',
    description: 'Creeks, riverbanks, and places to cast a line.',
  },
];

function initialArea() {
  try {
    const saved = JSON.parse(localStorage.getItem('go-last-location'));
    if (
      Number.isFinite(saved?.lat) &&
      Number.isFinite(saved?.lon) &&
      Math.abs(saved.lat) <= 90 &&
      Math.abs(saved.lon) <= 180
    ) {
      return {
        lat: saved.lat,
        lon: saved.lon,
        city: saved.city || 'Your selected area',
      };
    }
  } catch {
    /* A broken saved preference should never prevent browsing. */
  }
  return DEFAULT_AREA;
}

function OutingIllustration({ fishing }) {
  return (
    <svg
      viewBox="0 0 300 110"
      fill="none"
      aria-hidden="true"
      className="h-20 w-full sm:h-28"
    >
      <circle cx="239" cy="29" r="16" fill={fishing ? '#d8b27d' : '#d8c49b'} />
      <path
        d="M0 84 64 28 113 69 172 17 254 88 300 55V110H0Z"
        fill={fishing ? '#a8bcb7' : '#a7b39b'}
      />
      <path
        d="M0 96 90 70 171 87 245 66 300 88V110H0Z"
        fill={fishing ? '#637f79' : '#63765d'}
      />
      {fishing ? (
        <g stroke="#e7eee9" strokeWidth="2" strokeLinecap="round">
          <path d="M18 95h54m34 7h69m31-7h76M86 87h39" />
          <path d="m37 80 26-50q29 3 36 42" stroke="#354c43" />
        </g>
      ) : (
        <path d="M155 110q-24-17 0-23t6-22" stroke="#e4dcc7" strokeWidth="9" />
      )}
    </svg>
  );
}

export default function App() {
  const [area, setArea] = useState(initialArea);
  const [activity, setActivity] = useState(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedTotal, setSavedTotal] = useState(savedCount);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const locationRequest = useRef(0);
  const forecast = useLocalForecast(area);

  useEffect(() => {
    track('app_open');
    const refresh = () => setSavedTotal(savedCount());
    window.addEventListener('go:saved-places-changed', refresh);
    return () => window.removeEventListener('go:saved-places-changed', refresh);
  }, []);

  useEffect(() => {
    if (area.city !== 'Your selected area') return;
    let active = true;
    getCityFromCoords(area.lat, area.lon)
      .then((city) => {
        if (active) setArea((current) => ({ ...current, city }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [area.lat, area.lon, area.city]);

  async function changeLocation(resolve) {
    const request = ++locationRequest.current;
    setLocating(true);
    setLocationError('');
    try {
      const next = await resolve();
      if (request !== locationRequest.current) return;
      setArea(next);
      setEditingLocation(false);
      try {
        localStorage.setItem('go-last-location', JSON.stringify(next));
      } catch {
        /* Browsing still works when device storage is unavailable. */
      }
    } catch {
      if (request === locationRequest.current) {
        setLocationError(
          'We could not find that location. Try a nearby town or ZIP code.',
        );
      }
    } finally {
      if (request === locationRequest.current) setLocating(false);
    }
  }

  if (savedOpen) return <SavedPlacesView onClose={() => setSavedOpen(false)} />;
  if (activity) {
    return (
      <ActivityDiscoveryView
        activity={activity}
        area={area}
        forecast={forecast}
        onClose={() => setActivity(null)}
      />
    );
  }

  return (
    <div className="min-h-screen px-5 pb-10 sm:px-8">
      <header className="mx-auto flex max-w-2xl items-center justify-between border-b border-[#2d3b2d]/10 py-5">
        <a
          href="/"
          aria-label="GO home"
          className="text-3xl font-extrabold tracking-[-0.08em]"
        >
          GO<span className="text-[#7b8b62]">.</span>
        </a>
        <button
          onClick={() => setSavedOpen(true)}
          className="rounded-full border border-stone-200 bg-white/70 px-4 py-2 text-sm font-medium hover:border-stone-400"
        >
          Saved places{savedTotal > 0 ? ` (${savedTotal})` : ''}
        </button>
      </header>

      <main className="mx-auto max-w-2xl">
        <section className="pb-7 pt-9 sm:pt-12">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#63765d]">
            A Hudson Valley field guide
          </p>
          <h1 className="mt-4 max-w-lg font-serif text-[2.8rem] leading-[1.06] tracking-tight sm:text-6xl">
            What can I go do
            <br />
            right now?
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-stone-600">
            Find a hike or a fishing spot. Check the forecast, see the details,
            and head out.
          </p>
        </section>

        <section
          aria-label="Your area"
          className="mb-6 rounded-2xl border border-stone-200 bg-white/80 px-4 py-3"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-stone-600">
              Exploring{' '}
              <span className="font-semibold text-[#2d3b2d]">{area.city}</span>
            </p>
            <button
              onClick={() => setEditingLocation((value) => !value)}
              aria-expanded={editingLocation}
              className="shrink-0 py-1 text-sm font-medium underline underline-offset-4"
            >
              {editingLocation ? 'Close' : 'Change town'}
            </button>
          </div>
          {editingLocation && (
            <div className="border-t border-stone-100 pb-2 pt-4 mt-3">
              <LocationInput
                isLoading={locating}
                onSubmit={(zip) =>
                  changeLocation(() => getCoordinatesFromZip(zip))
                }
                onSubmitCity={(city) =>
                  changeLocation(() => getCoordinatesFromCity(city))
                }
                onUseLocation={(lat, lon, city) =>
                  changeLocation(async () => ({
                    lat,
                    lon,
                    city: city || (await getCityFromCoords(lat, lon)),
                  }))
                }
              />
              {locationError && (
                <p role="alert" className="mt-3 text-sm text-red-700">
                  {locationError}
                </p>
              )}
              <p className="mt-4 text-xs leading-relaxed text-stone-500">
                Our place guide covers the Hudson Valley. Location searches and
                weather requests are sent to our weather provider.
              </p>
            </div>
          )}
        </section>

        <section
          aria-label="Choose an outing"
          className="grid grid-cols-2 gap-3 sm:gap-4"
        >
          {OUTINGS.map((outing) => (
            <button
              key={outing.id}
              onClick={() => {
                track('activity_opened', { activity: outing.id });
                setActivity(activities.find((item) => item.id === outing.id));
              }}
              className="group overflow-hidden rounded-2xl border border-[#d5ddce] bg-[#f2f5ee] text-left transition-shadow hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2d3b2d]"
            >
              <OutingIllustration fishing={outing.id === 'fishing'} />
              <div className="p-3 sm:p-5">
                <h2 className="flex items-center justify-between gap-2 text-lg font-semibold sm:text-xl">
                  {outing.title}
                  <span
                    aria-hidden="true"
                    className="transition-transform group-hover:translate-x-1"
                  >
                    &rarr;
                  </span>
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">
                  {outing.description}
                </p>
              </div>
            </button>
          ))}
        </section>

        <div className="mt-7">
          <LocalForecast area={area} forecast={forecast} />
        </div>
        <footer className="mt-7 border-t border-stone-200 pt-5 text-xs leading-relaxed text-stone-500">
          A small guide, still growing. Place details may be incomplete; access,
          hours, and conditions can change. Saved places stay on this device.
        </footer>
      </main>
    </div>
  );
}
